import React, { useRef, useEffect, useState, useCallback } from 'react';
import API from '../services/api';

const POLL_INTERVAL = 3000;
const CANVAS_W = 1100;
const CANVAS_H = 420;

const BRUSH_SIZES = [
  { label: 'S', value: 2, dot: 7 },
  { label: 'M', value: 4, dot: 11 },
  { label: 'L', value: 8, dot: 17 },
];

const TEXT_SIZES = [
  { label: 'S', value: 18 },
  { label: 'M', value: 28 },
  { label: 'L', value: 44 },
];

function fmtWeek(weekStart) {
  if (!weekStart) return '';
  const d = new Date(weekStart + 'T00:00:00');
  const end = new Date(d);
  end.setDate(d.getDate() + 6);
  const opts = { month: 'short', day: 'numeric' };
  return `${d.toLocaleDateString('en-US', opts)} – ${end.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;
}

// ── TextPreview: draggable, rotatable overlay rendered during text placement ──
function TextPreview({ draft, onDragStart, onRotateStart }) {
  return (
    <div
      style={{
        position: 'absolute',
        left: draft.x * CANVAS_W,
        top: draft.y * CANVAS_H,
        transform: `translate(-50%, -50%) rotate(${draft.rotation}deg)`,
        cursor: 'move',
        userSelect: 'none',
        pointerEvents: 'all',
        zIndex: 10,
      }}
      onMouseDown={onDragStart}
      onTouchStart={onDragStart}
    >
      <span style={{
        fontFamily: 'Arial, Helvetica, sans-serif',
        fontSize: draft.fontSize,
        fontWeight: 700,
        color: draft.color,
        display: 'block',
        whiteSpace: 'pre',
        lineHeight: 1,
        pointerEvents: 'none',
        minWidth: 24,
        textShadow: '0px 1px 4px rgba(0,0,0,0.35)',
        outline: '1.5px dashed rgba(120,120,120,0.4)',
        outlineOffset: 4,
      }}>
        {draft.text || '   '}
      </span>
      {/* Rotation handle */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          right: -26,
          transform: 'translateY(-50%)',
          width: 14, height: 14,
          borderRadius: '50%',
          background: '#fff',
          border: '2px solid #9CA3AF',
          cursor: 'grab',
          boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
          pointerEvents: 'all',
          zIndex: 11,
        }}
        onMouseDown={e => { e.stopPropagation(); onRotateStart(e); }}
        onTouchStart={e => { e.stopPropagation(); onRotateStart(e); }}
      />
    </div>
  );
}

export default function CommunityCanvas() {
  // ── Canvas + stroke refs ────────────────────────────────────────────────
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const strokesRef = useRef([]);
  const textItemsRef = useRef([]);
  const activeStrokeRef = useRef([]);
  const lastStrokeIdRef = useRef(0);
  const lastTextIdRef = useRef(0);
  const isDrawingRef = useRef(false);
  const pollTimerRef = useRef(null);
  const mySessionStrokesRef = useRef([]);
  const mySessionTextRef = useRef([]);

  // Text drag / rotate refs (used inside stable event-listener closures)
  const isDraggingTextRef = useRef(false);
  const isRotatingTextRef = useRef(false);
  const dragStartRef = useRef(null);   // { mx, my, ox, oy }
  const rotateStartRef = useRef(null); // { cx, cy, startAngle, origRotation }
  const draftTextRef = useRef(null);   // always-current mirror of draftText state

  // ── Canvas live state ───────────────────────────────────────────────────
  const [userColors, setUserColors] = useState({ color1: '#888', color2: '#aaa' });
  const [selectedColor, setSelectedColor] = useState(null);
  const [brushSize, setBrushSize] = useState(4);
  const [weekStart, setWeekStart] = useState('');
  const [prompt, setPrompt] = useState(null);
  const [activeUsers, setActiveUsers] = useState([]);
  const [poolCount, setPoolCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [canvasReady, setCanvasReady] = useState(false);

  // ── View state ──────────────────────────────────────────────────────────
  const [view, setView] = useState('live');
  const [archives, setArchives] = useState([]);
  const [archivesLoading, setArchivesLoading] = useState(false);
  const [archiveDetail, setArchiveDetail] = useState(null);
  const [archiveDetailLoading, setArchiveDetailLoading] = useState(false);

  // ── Undo / redo ─────────────────────────────────────────────────────────
  const [undoStack, setUndoStack] = useState([]);
  const [myActionCount, setMyActionCount] = useState(0);

  // ── Text mode ───────────────────────────────────────────────────────────
  const [isTextMode, setIsTextMode] = useState(false);
  const [draftText, setDraftText] = useState(null); // { x, y, text, color, fontSize, rotation }
  const [textEditActive, setTextEditActive] = useState(false); // gate for global drag listeners

  // ── Prompt suggestion ───────────────────────────────────────────────────
  const [promptInput, setPromptInput] = useState('');
  const [promptStatus, setPromptStatus] = useState(null);

  // Keep draftTextRef in sync for use inside stable closures
  useEffect(() => { draftTextRef.current = draftText; }, [draftText]);

  // ── Draw helpers ─────────────────────────────────────────────────────────
  const drawStroke = useCallback((ctx, stroke, w, h) => {
    const pts = stroke.points;
    if (!pts || pts.length < 2) return;
    ctx.save();
    ctx.strokeStyle = stroke.color;
    ctx.lineWidth = stroke.stroke_width || 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(pts[0].x * w, pts[0].y * h);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x * w, pts[i].y * h);
    ctx.stroke();
    ctx.restore();
  }, []);

  const drawTextItem = useCallback((ctx, item, w, h) => {
    if (!item.text_content) return;
    // Scale font proportionally to canvas height so it always matches the HTML
    // preview regardless of actual canvas buffer dimensions.
    const px = Math.round(((item.font_size || 28) / CANVAS_H) * h);
    ctx.save();
    ctx.translate(item.x * w, item.y * h);
    ctx.rotate(((item.rotation || 0) * Math.PI) / 180);
    ctx.font = `bold ${px}px Arial, Helvetica, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = 'rgba(0,0,0,0.35)';
    ctx.shadowBlur = 4;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 1;
    ctx.fillStyle = item.color;
    ctx.fillText(item.text_content, 0, 0);
    ctx.restore();
  }, []);

  // Strokes rendered first; text overlays on top (like a caption layer)
  const redrawCanvas = useCallback((strokes, texts) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    (strokes || []).forEach(s => drawStroke(ctx, s, canvas.width, canvas.height));
    (texts || []).forEach(t => drawTextItem(ctx, t, canvas.width, canvas.height));
  }, [drawStroke, drawTextItem]);

  const redrawAll = useCallback(
    () => redrawCanvas(strokesRef.current, textItemsRef.current),
    [redrawCanvas]
  );

  // ── Initial canvas load ───────────────────────────────────────────────────
  useEffect(() => {
    let mounted = true;
    API.get('/drawing/canvas').then(res => {
      if (!mounted) return;
      const { strokes, textItems, userColors: uc, weekStart: ws, activeUsers: au, prompt: p, poolCount: pc } = res.data;
      setUserColors(uc);
      setSelectedColor(uc.color1);
      setWeekStart(ws);
      setActiveUsers(au || []);
      setPrompt(p || null);
      setPoolCount(pc || 0);
      strokesRef.current = strokes || [];
      textItemsRef.current = textItems || [];
      if (strokes?.length) lastStrokeIdRef.current = strokes[strokes.length - 1].id;
      if (textItems?.length) lastTextIdRef.current = textItems[textItems.length - 1].id;
      setLoading(false);
      setCanvasReady(true);
    }).catch(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  useEffect(() => { if (canvasReady && view === 'live') redrawAll(); }, [canvasReady, view, redrawAll]);

  // ── Live polling (strokes + text items) ─────────────────────────────────
  useEffect(() => {
    if (!canvasReady || view !== 'live') return;
    const poll = async () => {
      try {
        const res = await API.get(`/drawing/updates?strokeId=${lastStrokeIdRef.current}&textId=${lastTextIdRef.current}`);
        const newStrokes = res.data.strokes || [];
        const newTexts = res.data.textItems || [];

        if (newStrokes.length > 0) {
          const canvas = canvasRef.current;
          const ctx = canvas?.getContext('2d');
          newStrokes.forEach(s => {
            strokesRef.current.push(s);
            if (ctx) drawStroke(ctx, s, canvas.width, canvas.height);
          });
          lastStrokeIdRef.current = newStrokes[newStrokes.length - 1].id;
        }
        if (newTexts.length > 0) {
          newTexts.forEach(t => textItemsRef.current.push(t));
          lastTextIdRef.current = newTexts[newTexts.length - 1].id;
          redrawAll(); // text requires full redraw to composite correctly
        }
        if (newStrokes.length > 0 || newTexts.length > 0) {
          API.get('/drawing/canvas').then(r => setActiveUsers(r.data.activeUsers || [])).catch(() => {});
        }
      } catch { /* silent */ }
    };
    pollTimerRef.current = setInterval(poll, POLL_INTERVAL);
    return () => clearInterval(pollTimerRef.current);
  }, [canvasReady, view, drawStroke, redrawAll]);

  // ── Canvas resize ─────────────────────────────────────────────────────────
  // Use the CANVAS_W/H constants directly — the wrap is always that fixed size,
  // and offsetWidth can be stale (0 or 300 default) on first paint, causing text
  // drawn at e.g. 28 canvas-px to render 3× larger than the HTML preview.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const sync = () => {
      if (view === 'archive-detail') {
        canvas.width = CANVAS_W; canvas.height = CANVAS_H;
        if (archiveDetail) redrawCanvas(archiveDetail.strokes, archiveDetail.textItems);
      } else if (canvas.width !== CANVAS_W || canvas.height !== CANVAS_H) {
        canvas.width = CANVAS_W; canvas.height = CANVAS_H;
        if (view === 'live') redrawAll();
      }
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [view, archiveDetail, redrawAll, redrawCanvas, canvasReady]);

  // ── Global drag / rotate listeners for text placement ────────────────────
  useEffect(() => {
    if (!textEditActive) return;

    const onMove = (e) => {
      if (e.cancelable) e.preventDefault();
      const src = e.touches ? e.touches[0] : e;
      if (isDraggingTextRef.current && dragStartRef.current) {
        const { mx, my, ox, oy } = dragStartRef.current;
        const dx = (src.clientX - mx) / CANVAS_W;
        const dy = (src.clientY - my) / CANVAS_H;
        setDraftText(prev => prev ? {
          ...prev,
          x: Math.min(Math.max(ox + dx, 0.02), 0.98),
          y: Math.min(Math.max(oy + dy, 0.02), 0.98),
        } : null);
      } else if (isRotatingTextRef.current && rotateStartRef.current) {
        const { cx, cy, startAngle, origRotation } = rotateStartRef.current;
        const angle = Math.atan2(src.clientY - cy, src.clientX - cx) * (180 / Math.PI);
        setDraftText(prev => prev ? { ...prev, rotation: origRotation + angle - startAngle } : null);
      }
    };

    const onUp = () => {
      isDraggingTextRef.current = false;
      isRotatingTextRef.current = false;
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onUp);
    };
  }, [textEditActive]);

  // ── Escape key exits text mode ───────────────────────────────────────────
  useEffect(() => {
    if (!isTextMode) return;
    const onKey = (e) => {
      if (e.key === 'Escape') exitTextMode();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isTextMode]);

  // ── Archives ─────────────────────────────────────────────────────────────
  const loadArchives = useCallback(async () => {
    setArchivesLoading(true);
    try {
      const res = await API.get('/drawing/archives');
      setArchives(res.data.archives || []);
    } catch { setArchives([]); }
    finally { setArchivesLoading(false); }
  }, []);

  const enterArchives = useCallback(() => {
    setIsTextMode(false);
    setDraftText(null);
    setTextEditActive(false);
    setView('archives');
    setArchiveDetail(null);
    loadArchives();
  }, [loadArchives]);

  const enterArchiveDetail = useCallback(async (ws) => {
    setView('archive-detail');
    setArchiveDetailLoading(true);
    try {
      const res = await API.get(`/drawing/archive/${ws}`);
      setArchiveDetail(res.data);
    } catch { setView('archives'); }
    finally { setArchiveDetailLoading(false); }
  }, []);

  const backToLive = useCallback(() => {
    setView('live');
    setArchiveDetail(null);
    requestAnimationFrame(redrawAll);
  }, [redrawAll]);

  const backToArchives = useCallback(() => {
    setView('archives');
    setArchiveDetail(null);
    loadArchives();
  }, [loadArchives]);

  // ── Text mode helpers ─────────────────────────────────────────────────────
  function exitTextMode() {
    setIsTextMode(false);
    setDraftText(null);
    setTextEditActive(false);
    isDraggingTextRef.current = false;
    isRotatingTextRef.current = false;
  }

  function startTextDrag(e) {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    isDraggingTextRef.current = true;
    const src = e.touches ? e.touches[0] : e;
    const dt = draftTextRef.current;
    if (dt) dragStartRef.current = { mx: src.clientX, my: src.clientY, ox: dt.x, oy: dt.y };
  }

  function startTextRotate(e) {
    e.preventDefault();
    e.stopPropagation();
    isRotatingTextRef.current = true;
    const src = e.touches ? e.touches[0] : e;
    const dt = draftTextRef.current;
    const wrap = wrapRef.current;
    if (!dt || !wrap) return;
    const rect = wrap.getBoundingClientRect();
    const cx = rect.left + dt.x * CANVAS_W;
    const cy = rect.top + dt.y * CANVAS_H;
    const startAngle = Math.atan2(src.clientY - cy, src.clientX - cx) * (180 / Math.PI);
    rotateStartRef.current = { cx, cy, startAngle, origRotation: dt.rotation };
  }

  async function handleConfirmText() {
    const dt = draftTextRef.current;
    if (!dt || !dt.text.trim()) return;
    if (undoStack.length > 0) setUndoStack([]);
    try {
      const res = await API.post('/drawing/text', {
        text: dt.text,
        x: dt.x,
        y: dt.y,
        fontSize: dt.fontSize,
        color: dt.color,
        rotation: dt.rotation,
      });
      const saved = res.data.textItem;
      textItemsRef.current.push(saved);
      lastTextIdRef.current = saved.id;
      mySessionTextRef.current.push(saved);
      setMyActionCount(c => c + 1);
    } catch { /* silent — text placed locally even if save fails */ }
    exitTextMode();
    redrawAll();
  }

  // ── Drawing handlers ─────────────────────────────────────────────────────
  function getPoint(e) {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - rect.left) / rect.width, y: (src.clientY - rect.top) / rect.height };
  }

  function onPointerDown(e) {
    if (view !== 'live') return;
    if (isTextMode) {
      if (!draftText) {
        e.preventDefault();
        const pt = getPoint(e);
        const dt = { x: pt.x, y: pt.y, text: '', color: selectedColor, fontSize: 28, rotation: 0 };
        setDraftText(dt);
        setTextEditActive(true);
      }
      return;
    }
    e.preventDefault();
    isDrawingRef.current = true;
    activeStrokeRef.current = [getPoint(e)];
  }

  function onPointerMove(e) {
    if (view !== 'live' || isTextMode || !isDrawingRef.current) return;
    e.preventDefault();
    const pt = getPoint(e);
    activeStrokeRef.current.push(pt);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pts = activeStrokeRef.current;
    if (pts.length >= 2) {
      const prev = pts[pts.length - 2];
      ctx.save();
      ctx.strokeStyle = selectedColor;
      ctx.lineWidth = brushSize;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(prev.x * canvas.width, prev.y * canvas.height);
      ctx.lineTo(pt.x * canvas.width, pt.y * canvas.height);
      ctx.stroke();
      ctx.restore();
    }
  }

  async function onPointerUp(e) {
    if (isTextMode || !isDrawingRef.current) return;
    isDrawingRef.current = false;
    const pts = activeStrokeRef.current;
    activeStrokeRef.current = [];
    if (pts.length < 2) return;
    if (undoStack.length > 0) setUndoStack([]);
    try {
      const res = await API.post('/drawing/stroke', { color: selectedColor, points: pts, width: brushSize });
      const saved = res.data.stroke;
      strokesRef.current.push(saved);
      lastStrokeIdRef.current = saved.id;
      mySessionStrokesRef.current.push(saved);
      setMyActionCount(c => c + 1);
    } catch { redrawAll(); }
  }

  function onPointerLeave(e) {
    if (!isTextMode && isDrawingRef.current) onPointerUp(e);
  }

  // ── Undo / Redo ───────────────────────────────────────────────────────────
  async function handleUndo() {
    const lastStroke = mySessionStrokesRef.current[mySessionStrokesRef.current.length - 1];
    const lastText = mySessionTextRef.current[mySessionTextRef.current.length - 1];
    if (!lastStroke && !lastText) return;

    const useText = lastText && (!lastStroke || lastText.id > lastStroke.id);

    if (useText) {
      try { await API.delete(`/drawing/text/${lastText.id}`); } catch { /* already gone */ }
      mySessionTextRef.current.pop();
      textItemsRef.current = textItemsRef.current.filter(t => t.id !== lastText.id);
      setMyActionCount(c => c - 1);
      setUndoStack(prev => [...prev, { ...lastText, _undoType: 'text' }]);
    } else {
      try { await API.delete(`/drawing/stroke/${lastStroke.id}`); } catch { /* already gone */ }
      mySessionStrokesRef.current.pop();
      strokesRef.current = strokesRef.current.filter(s => s.id !== lastStroke.id);
      setMyActionCount(c => c - 1);
      setUndoStack(prev => [...prev, { ...lastStroke, _undoType: 'stroke' }]);
    }
    redrawAll();
  }

  async function handleRedo() {
    if (undoStack.length === 0) return;
    const item = undoStack[undoStack.length - 1];

    if (item._undoType === 'text') {
      try {
        const res = await API.post('/drawing/text', {
          text: item.text_content,
          x: item.x, y: item.y,
          fontSize: item.font_size,
          color: item.color,
          rotation: item.rotation,
        });
        const saved = res.data.textItem;
        textItemsRef.current.push(saved);
        lastTextIdRef.current = saved.id;
        mySessionTextRef.current.push(saved);
        setMyActionCount(c => c + 1);
        setUndoStack(prev => prev.slice(0, -1));
        redrawAll();
      } catch { /* silent */ }
    } else {
      try {
        const res = await API.post('/drawing/stroke', {
          color: item.color,
          points: item.points,
          width: item.stroke_width,
        });
        const saved = res.data.stroke;
        strokesRef.current.push(saved);
        lastStrokeIdRef.current = saved.id;
        mySessionStrokesRef.current.push(saved);
        setMyActionCount(c => c + 1);
        setUndoStack(prev => prev.slice(0, -1));
        redrawAll();
      } catch { /* silent */ }
    }
  }

  // ── Prompt suggestion ─────────────────────────────────────────────────────
  async function submitPrompt(e) {
    e.preventDefault();
    if (!promptInput.trim()) return;
    setPromptStatus('submitting');
    try {
      const res = await API.post('/drawing/prompt', { promptText: promptInput.trim() });
      setPoolCount(res.data.poolCount);
      setPromptInput('');
      setPromptStatus('success');
      setTimeout(() => setPromptStatus(null), 4000);
    } catch {
      setPromptStatus('error');
      setTimeout(() => setPromptStatus(null), 3000);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────
  const canUndo = myActionCount > 0;
  const canRedo = undoStack.length > 0;
  const isLive = view === 'live';
  const isArchives = view === 'archives';
  const isArchiveDetail = view === 'archive-detail';

  const currentPrompt = isArchiveDetail ? archiveDetail?.prompt : prompt;
  const currentWeekLabel = isArchiveDetail
    ? `Archive — ${fmtWeek(archiveDetail?.weekStart)}`
    : weekStart ? `Week of ${fmtWeek(weekStart)}` : '';

  const canvasCursor = isLive
    ? (isTextMode ? (draftText ? 'default' : 'text') : 'crosshair')
    : 'default';

  return (
    <div style={S.card}>
      {/* ── Header ── */}
      <div style={S.header}>
        <div style={S.headerLeft}>
          <span style={S.title}>{isArchives ? 'Canvas Archives' : 'Community Canvas'}</span>
          {!isArchives && <span style={S.weekLabel}>{currentWeekLabel}</span>}
        </div>
        <div style={S.headerRight}>
          {isLive && <button style={S.archiveBtn} onClick={enterArchives}>Archives</button>}
          {isArchives && <button style={S.backBtn} onClick={backToLive}>← Current Canvas</button>}
          {isArchiveDetail && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={S.backBtn} onClick={backToArchives}>← Archives</button>
              <button style={S.backBtn} onClick={backToLive}>Live Canvas</button>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div style={S.loading}>Loading canvas…</div>
      ) : (
        <>
          {/* ── Archives list ── */}
          {isArchives && (
            <div style={S.archivesPane}>
              {archivesLoading ? (
                <div style={S.loading}>Loading archives…</div>
              ) : archives.length === 0 ? (
                <div style={S.emptyArchive}>
                  <div style={{ fontSize: 36, marginBottom: 10 }}>🎨</div>
                  <div style={{ fontWeight: 600, color: '#374151', marginBottom: 6 }}>No archives yet</div>
                  <div style={{ color: '#9CA3AF', fontSize: 13 }}>Completed weeks will appear here every Monday.</div>
                </div>
              ) : (
                <div style={S.archiveGrid}>
                  {archives.map(a => (
                    <button key={a.canvas_week} style={S.archiveCard} onClick={() => enterArchiveDetail(a.canvas_week)}>
                      <div style={S.archiveCardDate}>{fmtWeek(a.canvas_week)}</div>
                      {a.prompt_text && <div style={S.archiveCardPrompt}>"{a.prompt_text}"</div>}
                      <div style={S.archiveCardMeta}>
                        {a.stroke_count} stroke{a.stroke_count !== 1 ? 's' : ''} · {a.artist_count} artist{a.artist_count !== 1 ? 's' : ''}
                      </div>
                      <div style={S.viewLink}>View →</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Toolbar (live mode only) ── */}
          {isLive && (
            <div style={S.toolbar}>
              <div style={S.toolGroup}>
                <span style={S.toolLabel}>Colors</span>
                {[userColors.color1, userColors.color2].map((c, i) => (
                  <button
                    key={i}
                    style={{ ...S.colorBtn, background: c, outline: selectedColor === c ? '3px solid #1D2C5F' : 'none', outlineOffset: 2 }}
                    onClick={() => {
                      setSelectedColor(c);
                      if (draftText) setDraftText(prev => prev ? { ...prev, color: c } : null);
                    }}
                    title={`Color ${i + 1}`}
                  />
                ))}
              </div>

              {/* Brush — hidden in text mode */}
              {!isTextMode && (
                <div style={S.toolGroup}>
                  <span style={S.toolLabel}>Brush</span>
                  {BRUSH_SIZES.map(bs => (
                    <button
                      key={bs.value}
                      onClick={() => setBrushSize(bs.value)}
                      style={{ ...S.brushBtn, background: brushSize === bs.value ? '#1D2C5F' : '#F3F4F6' }}
                    >
                      <span style={{ display: 'inline-block', width: bs.dot, height: bs.dot, borderRadius: '50%', background: brushSize === bs.value ? '#fff' : '#6B7280' }} />
                    </button>
                  ))}
                </div>
              )}

              {/* Text mode toggle */}
              <div style={S.toolGroup}>
                <button
                  style={{
                    ...S.textModeBtn,
                    background: isTextMode ? '#1D2C5F' : '#F3F4F6',
                    color: isTextMode ? '#fff' : '#374151',
                    boxShadow: isTextMode ? 'inset 0 1px 3px rgba(0,0,0,0.2)' : 'none',
                  }}
                  onClick={() => isTextMode ? exitTextMode() : setIsTextMode(true)}
                  title="Add text to canvas"
                >
                  <span style={{ fontFamily: 'Georgia, serif', fontWeight: 700, fontSize: 15 }}>T</span>
                </button>
              </div>

              {/* Text size — shown in text mode */}
              {isTextMode && draftText && (
                <div style={S.toolGroup}>
                  <span style={S.toolLabel}>Size</span>
                  {TEXT_SIZES.map(ts => (
                    <button
                      key={ts.value}
                      style={{
                        ...S.brushBtn,
                        background: draftText.fontSize === ts.value ? '#1D2C5F' : '#F3F4F6',
                        color: draftText.fontSize === ts.value ? '#fff' : '#374151',
                        fontSize: 11, fontWeight: 700, width: 28, height: 28,
                      }}
                      onClick={() => setDraftText(prev => prev ? { ...prev, fontSize: ts.value } : null)}
                    >
                      {ts.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Undo / Redo */}
              <div style={S.toolGroup}>
                <button style={{ ...S.undoRedoBtn, opacity: canUndo ? 1 : 0.35 }} disabled={!canUndo} onClick={handleUndo} title="Undo">
                  ↩ Undo
                </button>
                <button style={{ ...S.undoRedoBtn, opacity: canRedo ? 1 : 0.35 }} disabled={!canRedo} onClick={handleRedo} title="Redo">
                  ↪ Redo
                </button>
              </div>

              <div style={{ ...S.toolGroup, marginLeft: 'auto' }}>
                <span style={S.toolLabel}>Using</span>
                <span style={{ ...S.colorBtn, background: selectedColor, cursor: 'default', display: 'inline-block' }} />
              </div>
            </div>
          )}

          {/* ── Text mode hint / status bar ── */}
          {isLive && isTextMode && !draftText && (
            <div style={S.textHintBar}>
              <span style={{ fontSize: 13, color: '#374151' }}>
                Click anywhere on the canvas to place your text
              </span>
              <button style={S.textCancelBtn} onClick={exitTextMode}>✕ Cancel</button>
            </div>
          )}

          {/* ── Prompt banner ── */}
          {(isLive || isArchiveDetail) && currentPrompt && (
            <div style={S.promptBanner}>
              <span style={S.promptIcon}>✏️</span>
              <span style={S.promptText}>
                <strong>{isArchiveDetail ? "That week's prompt:" : "This week's prompt:"}</strong>{' '}{currentPrompt}
              </span>
            </div>
          )}

          {/* ── Canvas (live + archive-detail) ── */}
          {!isArchives && (
            <div style={S.canvasScrollOuter}>
              <div ref={wrapRef} style={{ ...S.canvasWrap, cursor: canvasCursor }}>
                {archiveDetailLoading && (
                  <div style={{ ...S.loading, position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    Loading archive…
                  </div>
                )}
                <canvas
                  ref={canvasRef}
                  style={{ ...S.canvas, opacity: archiveDetailLoading ? 0 : 1 }}
                  onMouseDown={onPointerDown}
                  onMouseMove={onPointerMove}
                  onMouseUp={onPointerUp}
                  onMouseLeave={onPointerLeave}
                  onTouchStart={onPointerDown}
                  onTouchMove={onPointerMove}
                  onTouchEnd={onPointerUp}
                />
                {/* Text placement overlay */}
                {isLive && draftText && (
                  <TextPreview
                    draft={draftText}
                    onDragStart={startTextDrag}
                    onRotateStart={startTextRotate}
                  />
                )}
              </div>
            </div>
          )}

          {/* ── Text input strip (shown while placing text) ── */}
          {isLive && isTextMode && draftText && (
            <div style={S.textInputStrip}>
              <input
                autoFocus
                type="text"
                value={draftText.text}
                onChange={e => setDraftText(prev => prev ? { ...prev, text: e.target.value } : null)}
                placeholder="Type your text…"
                maxLength={120}
                style={S.textStripInput}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleConfirmText();
                  if (e.key === 'Escape') exitTextMode();
                }}
              />
              <button
                style={{ ...S.textConfirmBtn, opacity: draftText.text.trim() ? 1 : 0.4 }}
                disabled={!draftText.text.trim()}
                onClick={handleConfirmText}
                title="Place text (Enter)"
              >
                ✓
              </button>
              <button style={S.textCancelBtn} onClick={exitTextMode} title="Cancel (Escape)">✕</button>
            </div>
          )}

          {/* ── Archive artists ── */}
          {isArchiveDetail && archiveDetail?.artists?.length > 0 && (
            <div style={S.legend}>
              <span style={S.toolLabel}>Artists that week:</span>
              {archiveDetail.artists.map((a, i) => (
                <span key={i} style={S.legendUser}>
                  <span style={{ ...S.legendDot, background: a.color1 }} />
                  <span style={{ ...S.legendDot, background: a.color2, marginLeft: 2 }} />
                  <span style={S.legendName}>{a.name}</span>
                </span>
              ))}
            </div>
          )}

          {/* ── Live: active users ── */}
          {isLive && activeUsers.length > 0 && (
            <div style={S.legend}>
              <span style={S.toolLabel}>This week:</span>
              {activeUsers.map(u => (
                <span key={u.id} style={S.legendUser}>
                  <span style={{ ...S.legendDot, background: u.color1 }} />
                  <span style={{ ...S.legendDot, background: u.color2, marginLeft: 2 }} />
                  <span style={S.legendName}>{u.name}</span>
                </span>
              ))}
            </div>
          )}

          {/* ── Suggest a prompt ── */}
          {isLive && (
            <div style={S.promptSection}>
              <div style={S.promptSectionTitle}>
                <span style={{ fontSize: 15 }}>💡</span>
                <span>Suggest a Prompt for a Future Week</span>
              </div>
              <form onSubmit={submitPrompt} style={S.promptForm}>
                <input
                  type="text"
                  value={promptInput}
                  onChange={e => setPromptInput(e.target.value)}
                  placeholder="e.g. Draw your morning commute, A rainy day scene…"
                  maxLength={200}
                  style={S.promptInput}
                  disabled={promptStatus === 'submitting'}
                />
                <button
                  type="submit"
                  disabled={!promptInput.trim() || promptStatus === 'submitting'}
                  style={{ ...S.promptSubmitBtn, opacity: !promptInput.trim() || promptStatus === 'submitting' ? 0.55 : 1 }}
                >
                  {promptStatus === 'submitting' ? 'Sending…' : 'Suggest'}
                </button>
              </form>
              {promptStatus === 'success' && <div style={S.promptSuccess}>Your prompt has been added to the pool!</div>}
              {promptStatus === 'error' && <div style={S.promptError}>Something went wrong. Please try again.</div>}
              {poolCount > 0 && promptStatus !== 'success' && (
                <div style={S.poolNote}>{poolCount} prompt{poolCount !== 1 ? 's' : ''} waiting in the pool</div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── Styles ─────────────────────────────────────────────────────────────── */
const S = {
  card: {
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    border: '1px solid #E5E7EB',
    overflow: 'hidden',
    marginBottom: 24,
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 20px', background: '#1D2C5F', color: '#fff',
  },
  headerLeft: { display: 'flex', flexDirection: 'column', gap: 2 },
  headerRight: { display: 'flex', alignItems: 'center', gap: 8 },
  title: { fontWeight: 700, fontSize: 15, letterSpacing: '0.02em' },
  weekLabel: { fontSize: 11, opacity: 0.75, letterSpacing: '0.04em' },
  archiveBtn: {
    background: 'rgba(255,255,255,0.15)', color: '#fff',
    border: '1px solid rgba(255,255,255,0.35)', borderRadius: 6,
    padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  backBtn: {
    background: 'rgba(255,255,255,0.15)', color: '#fff',
    border: '1px solid rgba(255,255,255,0.35)', borderRadius: 6,
    padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  loading: { padding: '2.5rem', textAlign: 'center', color: '#9CA3AF', fontSize: 14 },
  toolbar: {
    display: 'flex', alignItems: 'center', gap: 16,
    padding: '10px 16px', borderBottom: '1px solid #F3F4F6',
    flexWrap: 'wrap', background: '#FAFAF9',
  },
  toolGroup: { display: 'flex', alignItems: 'center', gap: 6 },
  toolLabel: {
    fontSize: 11, fontWeight: 600, color: '#6B7280',
    textTransform: 'uppercase', letterSpacing: '0.05em', marginRight: 2,
  },
  colorBtn: { width: 26, height: 26, borderRadius: '50%', border: 'none', cursor: 'pointer', padding: 0 },
  brushBtn: {
    width: 32, height: 32, borderRadius: 6, border: 'none', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  textModeBtn: {
    width: 32, height: 32, borderRadius: 6, border: 'none', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'background 0.15s',
  },
  // Text hint bar (shown when text mode is active but no draft yet)
  textHintBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '8px 16px', background: '#F0FDF4', borderBottom: '1px solid #BBF7D0',
    fontSize: 13, color: '#166534',
  },
  // Text input strip (shown while editing draft text)
  textInputStrip: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '10px 16px', background: '#F8FAFC', borderBottom: '1px solid #E2E8F0',
  },
  textStripInput: {
    flex: 1, padding: '8px 12px',
    border: '1.5px solid #CBD5E1', borderRadius: 6,
    fontSize: 14, color: '#111827', background: '#fff',
    outline: 'none', minWidth: 0,
  },
  textConfirmBtn: {
    padding: '7px 14px', background: '#1D2C5F', color: '#fff',
    border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 15,
    cursor: 'pointer', flexShrink: 0,
  },
  textCancelBtn: {
    padding: '7px 12px', background: '#F1F5F9', color: '#64748B',
    border: '1px solid #CBD5E1', borderRadius: 6, fontWeight: 600, fontSize: 13,
    cursor: 'pointer', flexShrink: 0,
  },
  promptBanner: {
    display: 'flex', alignItems: 'flex-start', gap: 8,
    padding: '10px 16px', background: '#EEF2FF', borderBottom: '1px solid #E0E7FF',
    fontSize: 13, color: '#3730A3',
  },
  promptIcon: { fontSize: 15, flexShrink: 0, marginTop: 1 },
  promptText: { lineHeight: 1.45 },
  canvasScrollOuter: { overflowX: 'auto' },
  canvasWrap: {
    position: 'relative',
    width: CANVAS_W,
    height: CANVAS_H,
    background: '#fff',
  },
  canvas: {
    display: 'block', width: '100%', height: '100%',
    touchAction: 'none', transition: 'opacity 0.15s',
  },
  legend: {
    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12,
    padding: '10px 16px', borderTop: '1px solid #F3F4F6', background: '#FAFAF9',
  },
  legendUser: { display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#374151' },
  legendDot: { display: 'inline-block', width: 10, height: 10, borderRadius: '50%' },
  legendName: { marginLeft: 2 },
  archivesPane: { padding: '20px' },
  emptyArchive: { textAlign: 'center', padding: '3rem 1rem', color: '#9CA3AF' },
  archiveGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 },
  archiveCard: {
    background: '#FAFAF9', border: '1px solid #E5E7EB', borderRadius: 10,
    padding: '14px 16px', textAlign: 'left', cursor: 'pointer', width: '100%',
  },
  archiveCardDate: { fontWeight: 700, color: '#1D2C5F', fontSize: 13, marginBottom: 4 },
  archiveCardPrompt: { fontSize: 12, color: '#6B7280', fontStyle: 'italic', marginBottom: 6, lineHeight: 1.4 },
  archiveCardMeta: { fontSize: 11, color: '#9CA3AF', marginBottom: 8 },
  viewLink: { fontSize: 12, color: '#C67A52', fontWeight: 600 },
  promptSection: { borderTop: '1px solid #F3F4F6', padding: '14px 16px', background: '#FAFAF9' },
  promptSectionTitle: { display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 10 },
  promptForm: { display: 'flex', gap: 8 },
  promptInput: {
    flex: 1, padding: '7px 11px', border: '1px solid #D1D5DB', borderRadius: 6,
    fontSize: 13, color: '#111827', background: '#fff', minWidth: 0,
  },
  promptSubmitBtn: {
    padding: '7px 16px', background: '#1D2C5F', color: '#fff',
    border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 13,
    cursor: 'pointer', whiteSpace: 'nowrap',
  },
  promptSuccess: { marginTop: 8, fontSize: 12, color: '#065F46', fontWeight: 600 },
  promptError: { marginTop: 8, fontSize: 12, color: '#991B1B' },
  poolNote: { marginTop: 6, fontSize: 11, color: '#9CA3AF' },
  undoRedoBtn: {
    display: 'flex', alignItems: 'center', gap: 4,
    padding: '4px 10px', borderRadius: 6, border: '1px solid #D1D5DB',
    background: '#fff', fontSize: 12, fontWeight: 600, color: '#374151',
    cursor: 'pointer', whiteSpace: 'nowrap',
  },
};
